import {
  FontAwesomeIcon,
  type FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome'
import {
  Box,
  type BoxProps,
  type MantineColor,
  type MantineSize,
  ThemeIcon,
} from '@mantine/core'
import { clsx } from 'clsx'
import { type ReactNode } from 'react'

import styles from './styles.module.css'

export type IconProps = BoxProps & {
  color?: MantineColor
  icon: FontAwesomeIconProps['icon']
  size?: MantineSize
  withCircleBackground?: boolean
  withFilledBackground?: boolean
  withSquareBackground?: boolean
}

export function Icon({
  icon,
  size = 'sm',
  withCircleBackground = false,
  withFilledBackground = false,
  withSquareBackground = false,
  ...props
}: IconProps): ReactNode {
  const hasBackground = withCircleBackground || withSquareBackground
  const iconClasses = clsx(styles.iconWrapper, props.className, styles[size])

  if (!hasBackground) {
    return (
      <Box {...props} c={props.color} className={iconClasses}>
        <FontAwesomeIcon
          height={props.h as number}
          icon={icon}
          // @ts-expect-error Not 100% compatible types
          size={size}
          width={props.w as number}
        />
      </Box>
    )
  }

  // When icon has BG (squared/circled)
  const fc = !props.color ? 'gray.7' : `${props.color}.7`
  const bg = !props.color ? 'gray.5' : `${props.color}.5`
  const variant = withFilledBackground ? 'filled' : 'light'
  const iconWithBgClasses = clsx(iconClasses, {
    [styles.hasBackground]: hasBackground,
    [styles.isRounded]: withCircleBackground,
    [styles.isSquared]: withSquareBackground,
  })

  return (
    <ThemeIcon
      {...props}
      c={fc}
      className={iconWithBgClasses}
      color={bg}
      variant={variant}
    >
      <FontAwesomeIcon icon={icon} />
    </ThemeIcon>
  )
}
